/**
 * Hierarchy Tree View - Collapsible tree visualization
 *
 * FERP Principles:
 * - Data-driven: All data from hierarchy prop
 * - No hardcoded business logic
 * - Complete type safety
 * - Real-time validation
 *
 * GitHub Issue: #340
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Stack,
  TextInput,
  Box,
  Group,
  ActionIcon,
  Badge,
  Text,
  Tooltip,
  ThemeIcon,
  Modal,
  Select,
  Button,
  Alert,
  ScrollArea,
} from '@mantine/core';
import {
  IconSearch,
  IconChevronRight,
  IconChevronDown,
  IconUser,
  IconShield,
  IconAlertTriangle,
  IconEdit,
  IconDeviceFloppy,
  IconX,
} from '@tabler/icons-react';
import {
  createHierarchyRepository,
  type RoleHierarchy,
  type RoleNode,
} from '../../data/repositories/HierarchyRepository';

export interface HierarchyTreeViewProps {
  hierarchy: RoleHierarchy;
  tenantId: string;
  currentUser: string;
  onHierarchyChange: () => void;
}

export function HierarchyTreeView({
  hierarchy,
  tenantId,
  currentUser,
  onHierarchyChange,
}: HierarchyTreeViewProps) {
  const repository = useMemo(
    () => createHierarchyRepository(),
    []
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRoles, setExpandedRoles] = useState<Set<string>>(
    new Set(hierarchy.rootRoleIds)
  );
  const [editingRole, setEditingRole] = useState<RoleNode | null>(null);
  const [newParentId, setNewParentId] = useState<string>('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Build role map for quick lookup
  const roleMap = useMemo(() => {
    return new Map(hierarchy.roles.map((role) => [role.roleId, role]));
  }, [hierarchy.roles]);

  // Filter roles by search query
  const matchesSearch = useCallback(
    (role: RoleNode): boolean => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        role.roleName.toLowerCase().includes(query) ||
        role.displayName.toLowerCase().includes(query) ||
        Boolean(role.description && role.description.toLowerCase().includes(query))
      );
    },
    [searchQuery]
  );

  // Toggle role expansion
  const toggleRole = useCallback((roleId: string) => {
    setExpandedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) {
        next.delete(roleId);
      } else {
        next.add(roleId);
      }
      return next;
    });
  }, []);

  // Get category color
  const getCategoryColor = (category: string): string => {
    switch (category.toLowerCase()) {
      case 'admin':
        return 'red';
      case 'editor':
        return 'blue';
      case 'viewer':
        return 'green';
      default:
        return 'gray';
    }
  };

  // Handle parent update
  const handleUpdateParent = useCallback(async () => {
    if (!editingRole) return;

    try {
      setSubmitting(true);
      setValidationError(null);

      // Validate if new parent would create cycle
      if (newParentId && newParentId !== editingRole.parentIds[0]) {
        const validation = await repository.validateParentUpdate(
          tenantId,
          editingRole.roleId,
          newParentId
        );

        if (!validation.valid) {
          setValidationError(validation.error || 'Invalid parent selection');
          return;
        }
      }

      // Update parent
      await repository.updateParent({
        tenantId,
        roleId: editingRole.roleId,
        newParentId: newParentId || null,
        updatedBy: currentUser,
      });

      // Reload hierarchy
      onHierarchyChange();

      // Close modal
      setEditingRole(null);
      setNewParentId('');
      setValidationError(null);
    } catch (err) {
      setValidationError(
        err instanceof Error ? err.message : 'Failed to update parent'
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    editingRole,
    newParentId,
    repository,
    tenantId,
    currentUser,
    onHierarchyChange,
  ]);

  // Render tree node recursively
  const renderTreeNode = useCallback(
    (role: RoleNode, depth: number): JSX.Element | null => {
      const isExpanded = expandedRoles.has(role.roleId);
      const hasChildren = role.childIds.length > 0;
      const hasIssues = hierarchy.validationIssues.some(
        (issue) => issue.roleId === role.roleId
      );
      const matches = matchesSearch(role);

      // If search active and doesn't match, skip
      if (searchQuery && !matches) {
        // Check if any children match
        const childrenMatch = role.childIds.some((childId) => {
          const child = roleMap.get(childId);
          return child && matchesSearch(child);
        });

        if (!childrenMatch) return null;
      }

      return (
        <Box key={role.roleId} mb="xs">
          {/* Role row */}
          <Group
            gap="xs"
            style={{
              marginLeft: depth * 24,
              padding: '8px',
              borderRadius: '4px',
              backgroundColor: matches && searchQuery ? '#fff3cd' : 'transparent',
            }}
          >
            {/* Expand/collapse button */}
            <ActionIcon
              size="sm"
              variant="subtle"
              onClick={() => toggleRole(role.roleId)}
              disabled={!hasChildren}
              style={{ opacity: hasChildren ? 1 : 0, visibility: hasChildren ? 'visible' : 'hidden' }}
            >
              {isExpanded ? (
                <IconChevronDown size={16} />
              ) : (
                <IconChevronRight size={16} />
              )}
            </ActionIcon>

            {/* Category badge */}
            <Badge size="sm" color={getCategoryColor(role.category)}>
              {role.category}
            </Badge>

            {/* Role name */}
            <Text fw={500}>{role.displayName}</Text>

            <Text size="sm" c="dimmed">
              ({role.roleName})
            </Text>

            {/* User count */}
            <Badge
              size="sm"
              variant="light"
              leftSection={<IconUser size={12} />}
            >
              {role.userCount}
            </Badge>

            {/* Permission count */}
            <Badge
              size="sm"
              variant="light"
              leftSection={<IconShield size={12} />}
            >
              {role.permissionCount}
            </Badge>

            {/* Issue indicator */}
            {hasIssues && (
              <Tooltip label="Has validation issues">
                <ThemeIcon size="sm" color="red" variant="light">
                  <IconAlertTriangle size={14} />
                </ThemeIcon>
              </Tooltip>
            )}

            {/* Disabled indicator */}
            {!role.enabled && (
              <Badge size="sm" color="gray">
                Disabled
              </Badge>
            )}

            {/* Edit button */}
            <Tooltip label="Edit parent">
              <ActionIcon
                size="sm"
                variant="light"
                onClick={() => {
                  setEditingRole(role);
                  setNewParentId(role.parentIds[0] || '');
                }}
              >
                <IconEdit size={14} />
              </ActionIcon>
            </Tooltip>
          </Group>

          {/* Children */}
          {hasChildren && isExpanded && (
            <Box>
              {role.childIds.map((childId) => {
                const child = roleMap.get(childId);
                if (!child) return null;
                return renderTreeNode(child, depth + 1);
              })}
            </Box>
          )}
        </Box>
      );
    },
    [
      expandedRoles,
      hierarchy.validationIssues,
      roleMap,
      searchQuery,
      matchesSearch,
      toggleRole,
    ]
  );

  return (
    <>
      <Stack gap="md">
        {/* Search */}
        <TextInput
          placeholder="Search roles by name or description..."
          leftSection={<IconSearch size={16} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          rightSection={
            searchQuery && (
              <ActionIcon
                size="sm"
                variant="subtle"
                onClick={() => setSearchQuery('')}
              >
                <IconX size={14} />
              </ActionIcon>
            )
          }
        />

        {/* Tree */}
        <ScrollArea h={500}>
          <Stack gap={0}>
            {hierarchy.rootRoleIds.map((rootId) => {
              const root = roleMap.get(rootId);
              if (!root) return null;
              return renderTreeNode(root, 0);
            })}
          </Stack>
        </ScrollArea>

        {/* Help text */}
        <Text size="xs" c="dimmed">
          Click the arrow to expand/collapse roles. Click the edit icon to change a
          role&apos;s parent.
        </Text>
      </Stack>

      {/* Edit Parent Modal */}
      <Modal
        opened={editingRole !== null}
        onClose={() => {
          setEditingRole(null);
          setNewParentId('');
          setValidationError(null);
        }}
        title={`Edit Parent for ${editingRole?.displayName}`}
        size="md"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Change the parent role to reorganize the hierarchy. Leave empty to make
            this a root role (no parent).
          </Text>

          {/* Current parent info */}
          {editingRole && editingRole.parentIds.length > 0 && (
            <Alert color="blue" variant="light">
              <Text size="sm">
                <strong>Current parent:</strong>{' '}
                {roleMap.get(editingRole.parentIds[0] ?? '')?.displayName || 'Unknown'}
              </Text>
            </Alert>
          )}

          {/* New parent select */}
          <Select
            label="New Parent Role"
            placeholder="Select parent role (or leave empty for root)"
            description="Choose a new parent or clear to make this a root role"
            data={[
              { value: '', label: '(Root Role - No Parent)' },
              ...hierarchy.roles
                .filter((r) => r.roleId !== editingRole?.roleId)
                .map((r) => ({
                  value: r.roleId,
                  label: `${r.displayName} (${r.roleName})`,
                })),
            ]}
            value={newParentId}
            onChange={(value) => setNewParentId(value || '')}
            searchable
            clearable
          />

          {/* Validation error */}
          {validationError && (
            <Alert icon={<IconX size={16} />} color="red" variant="light">
              {validationError}
            </Alert>
          )}

          {/* Actions */}
          <Group justify="flex-end" gap="xs">
            <Button
              variant="light"
              onClick={() => {
                setEditingRole(null);
                setNewParentId('');
                setValidationError(null);
              }}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={handleUpdateParent}
              loading={submitting}
            >
              Save Changes
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
